import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/types/api.types';
import { Button } from '@components/ui/Button';

import { ScheduleRow } from '../components/ScheduleRow';
import { buildHoursRequest, useBusinessHours } from '../hooks/useBusinessHours';
import { useBusinessSettingsStore } from '../store/businessSettingsStore';
import { DAYS_OF_WEEK, DEFAULT_SCHEDULE } from '../types';
import type { DayOfWeek, DaySchedule, ScheduleData } from '../types';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowLeft() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M13 8H3M7 12l-4-4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Step4Schedule({ onNext, onBack }: Props) {
  const { t } = useTranslation();
  const store = useBusinessSettingsStore();
  const { submitHours, isPending } = useBusinessHours();
  const [schedule, setSchedule] = useState<ScheduleData>(store.schedule ?? DEFAULT_SCHEDULE);
  const [serverError, setServerError] = useState<string | null>(null);

  function handleChange(day: DayOfWeek, updated: DaySchedule) {
    setSchedule((prev) => ({ ...prev, [day]: updated }));
  }

  const hasError = DAYS_OF_WEEK.some((day) => {
    const s = schedule[day];
    return s.open && s.from >= s.to;
  });

  function handleContinue() {
    setServerError(null);
    submitHours(buildHoursRequest(schedule), {
      onSuccess: () => {
        store.setSchedule(schedule);
        store.complete();
        onNext();
      },
      onError: (error) => {
        const apiError = error as ApiError;
        if (apiError.code === 'FORBIDDEN') {
          setServerError(t('onboarding.step4.forbidden'));
        } else {
          setServerError(t('onboarding.step4.hoursSaveFailed'));
        }
      },
    });
  }

  return (
    <div>
      <h2 className="font-display text-[2rem] font-semibold leading-tight text-foreground">
        {t('onboarding.step4.title')}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.step4.subtitle')}</p>

      <div className="mt-8 space-y-2">
        {DAYS_OF_WEEK.map((day) => (
          <ScheduleRow key={day} day={day} schedule={schedule[day]} onChange={handleChange} />
        ))}
      </div>

      {serverError && (
        <div role="alert" className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {serverError}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button type="button" variant="ghost" size="lg" leftIcon={<ArrowLeft />} onClick={onBack} disabled={isPending}>
          {t('onboarding.back')}
        </Button>
        <Button
          type="button"
          size="lg"
          rightIcon={<ArrowRight />}
          onClick={handleContinue}
          disabled={hasError}
          isLoading={isPending}
        >
          {t('onboarding.continue')}
        </Button>
      </div>
    </div>
  );
}
